import type {
	AssessmentResultProgressAppearance,
	AssessmentResultProgressData,
} from './assessment-result-progress';
import type {
	FastshiftAssessmentBlockerStatus,
	FastshiftAssessmentRawMetric,
	FastshiftAssessmentResult,
	FastshiftAssessmentRisk,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

const ASSESSMENT_RESULT_PROGRESS_BAR_IDS = new Set([
	'totalUsersPerOrg',
	'jswLicensedActiveUsers',
	'totalGroupsPerIdentityProviderDirectory',
	'confluenceTotalUsersPerOrgCustomer',
	'confluenceTotalGroupsPerIdentityProviderDirectoryCustomer',
	'numberOfActiveIssues',
	'numberOfCustomfields',
	'numberOfProjectRoleActors',
	'jsmTotalNumberOfSchemas',
	'totalAssets',
	'jsmTotalRequestTypes',
	'activeAgentsLimit',
]);

export type AssessmentResultCardViewModel = {
	id: string;
	dataPoint: string;
	dataPointSummary?: string;
	valueText: string;
	status: FastshiftAssessmentRisk | null;
	blockerStatus: FastshiftAssessmentBlockerStatus;
	hasRecommendations: boolean;
	progress: AssessmentResultProgressData | null;
};

const getNumericRawMetric = (rawMetric: FastshiftAssessmentRawMetric | undefined) => {
	if (typeof rawMetric === 'number') {
		return Number.isFinite(rawMetric) ? rawMetric : null;
	}

	if (typeof rawMetric !== 'string' || rawMetric.trim() === '') {
		return null;
	}

	const numericValue = Number(rawMetric);

	return Number.isFinite(numericValue) ? numericValue : null;
};

const getProgressAppearance = (value: number, limit: number): AssessmentResultProgressAppearance =>
	value >= limit * 1.2 ? 'error' : value > limit ? 'warning' : 'default';

export const mapAssessmentResultCardViewModel = (
	result: FastshiftAssessmentResult,
	formatNumber: (value: number) => string,
): AssessmentResultCardViewModel => {
	const value = getNumericRawMetric(result.rawMetricValue);
	const limit = getNumericRawMetric(result.rawMetricLimitHighRisk);
	const canShowProgress =
		ASSESSMENT_RESULT_PROGRESS_BAR_IDS.has(result.id) &&
		value !== null &&
		value >= 0 &&
		limit !== null &&
		limit > 0;
	let progress: AssessmentResultProgressData | null = null;
	let valueText = result.customerData ?? '';

	if (canShowProgress) {
		progress = {
			percentage: Math.round((value / limit) * 100),
			appearance: getProgressAppearance(value, limit),
		};
		valueText = `${formatNumber(value)} / ${formatNumber(limit)}`;
	}

	return {
		id: result.id,
		dataPoint: result.dataPoint ?? '',
		dataPointSummary: result.dataPointSummary,
		valueText,
		status: result.impact?.status ?? null,
		blockerStatus: result.blockerStatus,
		hasRecommendations: result.hasRecommendations,
		progress,
	};
};
